import React, { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ChatInput from './ChatInput';
import ChatMessages from './ChatMessage';
import LoggedInUserWrapper from '../../core/LoggedInUserWrapper';
import { useAddQueryParams, useAuth, useStudentChat, useParams, useApp } from '../../../hooks';
import {
  SOCKET_MESSAGE_TYPES,
  SOCKET_STATUS,
  STUDENT_CHAT_ERROR_TYPE,
  STUDENT_CHAT_MESSAGE_TYPES
} from '../../../constants/socket-constants';
import { scrollIntoElement } from '../../../utils/domUtils';
import { FullPageLoader } from '..';
import { useConsumption, useGetPracticeQuestionTopics } from '../../../queries';
import { useStableCallback } from '../../../hooks';

const ChatMain = (props) => {
  const { title, chatType } = props;
  const {
    refetch: refetchConsumptionData,
    isCreditExhauted,
    isLoading: isConsumptionLoading
  } = useConsumption();
  const { showPlans } = useApp();
  const { data: practiceTopic } = useGetPracticeQuestionTopics();
  const [messages, setMessages] = useState([]);
  const [shouldResume, setShouldResume] = useState(false);
  const [currentMessageId, setCurrentMessageId] = useState('');
  const { chatTopicId, chatId } = useParams();
  const addQueryParam = useAddQueryParams();
  const { user } = useAuth();
  const currentMessageIdRef = useRef();
  const lastMessage = useRef(null);
  const onMessageRef = useRef(() => {});
  const onReconnectionRef = useRef(() => {});
  const hasAutoOpenedPaywall = useRef(false);

  currentMessageIdRef.current = currentMessageId;

  // Auto-open paywall on chat when daily free credits are exhausted
  React.useEffect(() => {
    if (isConsumptionLoading || !isCreditExhauted || hasAutoOpenedPaywall.current) {
      return;
    }
    hasAutoOpenedPaywall.current = true;
    showPlans();
  }, [isConsumptionLoading, isCreditExhauted, showPlans]);

  const { initialize, sendStudentMessage, endChat, status, isInitialized, resumeChat } =
    useStudentChat({
      onMessageRef,
      onReconnectionRef
    });

  const topicName = React.useMemo(() => {
    return practiceTopic
      ? practiceTopic.find((topic) => topic._id === chatTopicId)?.topic || ''
      : '';
  }, [chatTopicId, practiceTopic]);

  const addNewEmptyMessage = useCallback(() => {
    const newId = uuidv4();
    setCurrentMessageId(newId);
    setMessages((prevState) => [
      ...prevState,
      { id: newId, message: '', displayName: 'Classwise' }
    ]);
  }, []);

  const removeLastMessage = useCallback(() => {
    setMessages((state) => state.slice(0, -1));
  }, []);

  const sendStudentMessageHandler = useCallback(
    (msgStr) => {
      sendStudentMessage(msgStr);
      addNewEmptyMessage();
    },
    [addNewEmptyMessage, sendStudentMessage]
  );

  React.useEffect(() => {
    if (chatId) {
      setShouldResume(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // Page refresh scenario
    if (status === SOCKET_STATUS.READY && shouldResume && chatId) {
      resumeChat({ threadId: chatId, shouldFetchMessages: true });
    }
  }, [status, chatId, resumeChat, shouldResume]);

  React.useEffect(() => {
    if (status === SOCKET_STATUS.READY && !isInitialized && !chatId) {
      initialize({ topicId: chatTopicId, chatType });
      addNewEmptyMessage();
      onReconnectionRef.current = () => {
        resumeChat({ threadId: chatId, shouldFetchMessages: false });
      };
    }
  }, [
    initialize,
    status,
    chatTopicId,
    addNewEmptyMessage,
    isInitialized,
    chatId,
    resumeChat,
    chatType
  ]);

  const selectMcqOption = useStableCallback((option) => {
    console.log('option', option);
    const chatId = uuidv4();
    if (option !== '')
      setMessages((prevState) => [
        ...prevState,
        ...[
          {
            id: chatId,
            message: option,
            isSentByUser: true,
            photoURL: user?.photoURL,
            displayName: user?.displayName
          }
        ]
      ]);
    sendStudentMessageHandler(option);
    setTimeout(() => {
      const ele = document.getElementById(chatId);
      ele.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  });

  onMessageRef.current = React.useCallback(
    (messagePayload) => {
      const { type: chatName, payload } = messagePayload;
      if (chatName === SOCKET_MESSAGE_TYPES.STUDENT_CHAT) {
        const { type: chatType, data } = payload;

        switch (chatType) {
          case STUDENT_CHAT_MESSAGE_TYPES.INIT:
            if (data?.threadId) {
              addQueryParam({ chatId: data?.threadId });
            }
            break;
          case STUDENT_CHAT_MESSAGE_TYPES.MESSAGE:
            setMessages((prevState) => {
              const existingMessageIndex = prevState.findIndex(
                ({ id }) => id === currentMessageIdRef.current
              );
              const existingMessage = prevState[existingMessageIndex];
              if (!existingMessage) {
                console.error('cannot find a message for this response');
                return prevState;
              }
              const newMessages = [...prevState];
              newMessages[existingMessageIndex] = {
                id: existingMessage.id,
                message: `${existingMessage.message}${data}`
              };
              return newMessages;
            });
            lastMessage.current.scrollIntoView({ behavior: 'smooth' });
            break;
          case STUDENT_CHAT_MESSAGE_TYPES.RESUME:
            if (data?.messages && data?.messages?.length > 0) {
              let chatId = null;
              const fetchedMessages = data.messages.map((message) => {
                const { text, role } = message;
                const isStudentMessage = role === 'student';
                chatId = uuidv4();
                return {
                  id: chatId,
                  message: text,
                  isSentByUser: isStudentMessage,
                  photoURL: isStudentMessage ? user?.photoURL : undefined,
                  displayName: isStudentMessage ? user?.displayName : 'Classwise'
                };
              });
              setMessages(fetchedMessages);
              scrollIntoElement(chatId);
              setShouldResume(false);
            }
            break;
          case STUDENT_CHAT_MESSAGE_TYPES.END:
            refetchConsumptionData();
            endChat();
            setCurrentMessageId('');
            break;
          case STUDENT_CHAT_ERROR_TYPE.CREDIT_LIMIT_EXHAUSTED:
            refetchConsumptionData();
            showPlans();
            removeLastMessage();
            break;
          case STUDENT_CHAT_MESSAGE_TYPES.ERROR:
            console.error('AI chat error: ', data);
            if (
              data === STUDENT_CHAT_ERROR_TYPE.CREDIT_LIMIT_EXHAUSTED ||
              data?.type === STUDENT_CHAT_ERROR_TYPE.CREDIT_LIMIT_EXHAUSTED ||
              data?.error === STUDENT_CHAT_ERROR_TYPE.CREDIT_LIMIT_EXHAUSTED
            ) {
              refetchConsumptionData();
              showPlans();
              removeLastMessage();
            }
            break;
        }
      }
    },
    [endChat, addQueryParam, user, refetchConsumptionData, showPlans, removeLastMessage]
  );

  if (shouldResume) {
    return <FullPageLoader />;
  }

  return (
    <LoggedInUserWrapper
      headerProps={{
        pageTitle: (
          <div className="mx-auto w-full min-w-0 max-w-full text-center">
            <p className="truncate text-sm font-semibold leading-tight text-foreground md:text-base">
              {title}
            </p>
            {topicName ? (
              <p className="truncate text-xs leading-tight text-muted-foreground">{topicName}</p>
            ) : null}
          </div>
        )
      }}
    >
      <div className="mx-auto flex h-full min-h-0 w-full max-w-5xl flex-col px-2 md:px-4 lg:max-w-6xl">
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pt-3 pb-24 md:pb-36 md:pt-4">
          <ChatMessages
            scrollRef={lastMessage}
            messages={messages}
            chatType="chats"
            selectMcqOption={selectMcqOption}
          />
        </div>
        <ChatInput
          scrollRef={lastMessage}
          chatType="chats"
          setMessages={setMessages}
          sendStudentMessage={sendStudentMessageHandler}
        />
      </div>
    </LoggedInUserWrapper>
  );
};

export default ChatMain;
